import { useState, useEffect } from 'react';
import { Lock, Users, Check, AlertTriangle, Pill, UserCheck, Syringe } from 'lucide-react';
import { useTeamToday, useEmployees } from '@/api/resources';
import { useLogout } from '@/api/auth';
import { Chip } from '@/components/ui/Chip';
import { Button } from '@/components/ui/Button';

export default function TeamWorkstationPage() {
  const logout = useLogout();
  const { data: teamData, isLoading } = useTeamToday();
  const { data: employees } = useEmployees();

  // Horloge live
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const timeLabel = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }).replace(':', 'h');
  const dateLabel = now.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const missions = teamData?.morning?.assignments ?? [];
  const presentIds = teamData?.morning?.presentEmployeeIds ?? [];

  const missionLabels: Record<string, { color: string; name: string }> = {
    COMPTOIR: { color: '#1e3a5f', name: 'Comptoir' },
    BACKOFFICE_PHARMA: { color: '#1e3a5f', name: 'Back office pharmacie' },
    BACKOFFICE_TECHNIQUE: { color: '#6b7a8f', name: 'Back office technique' },
    VACCINATION: { color: '#ff6b57', name: 'Vaccination' },
    PDA: { color: '#c026d3', name: 'PDA' },
    PREPARATOIRE: { color: '#ea580c', name: 'Préparatoire' },
    OUVERTURE: { color: '#1e3a5f', name: 'Ouverture' },
    FERMETURE: { color: '#1e3a5f', name: 'Fermeture' },
  };

  return (
    <div className="w-full h-screen flex flex-col" style={{ backgroundColor: '#f4f6fa' }}>
      {/* Topbar */}
      <div className="border-b border-slate-200 bg-white px-5 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-accent flex items-center justify-center">
            <Users size={14} color="#fff" strokeWidth={2.25} />
          </div>
          <div>
            <div className="text-[13px] font-semibold">
              Officina <span className="text-[10px] font-normal text-accent ml-1 uppercase tracking-[0.1em]">Mode équipe</span>
            </div>
            <div className="text-[10px] text-slate-500">Poste partagé</div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[18px] font-semibold tabular-nums leading-none">{timeLabel}</div>
            <div className="text-[10px] text-slate-500">{dateLabel}</div>
          </div>
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-primary-bg rounded-full">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-[11px] font-medium text-primary">{presentIds.length} présents</span>
          </div>
          <Button variant="default" icon={Lock} size="sm" onClick={logout}>Verrouiller</Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-5 max-w-[1200px] mx-auto w-full">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-medium mb-1">
              Organisation
            </div>
            <h2 className="text-[28px] font-semibold tracking-tight leading-tight">
              <span className="text-primary">Aujourd'hui</span> — matin
            </h2>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-0.5">
              Statut pharmacie
            </div>
            <Chip variant="green" size="md">
              <Check size={11} /> Effectifs OK
            </Chip>
          </div>
        </div>

        {isLoading && <div className="text-[12px] text-slate-500">Chargement…</div>}

        <div className="grid grid-cols-[1.5fr_1fr] gap-4">
          {/* Missions */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-[14px] font-semibold">Qui est où</div>
              <Chip variant="default">{missions.length} missions</Chip>
            </div>
            <div className="divide-y divide-slate-100">
              {missions.map((m) => {
                const info = missionLabels[m.mission] ?? { color: '#64748b', name: m.mission };
                const covered = m.status === 'COVERED';
                return (
                  <div key={m.mission} className="px-5 py-4">
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: info.color }} />
                        <div className="text-[14.5px] font-semibold">{info.name}</div>
                        <span className="text-[11px] text-slate-500 tabular-nums">
                          {m.employeeIds.length} / {m.targetCount}
                        </span>
                      </div>
                      {covered ? (
                        <Chip variant="green" size="md"><Check size={11} /> Couvert</Chip>
                      ) : (
                        <Chip variant="amber" size="md"><AlertTriangle size={11} /> Non couvert</Chip>
                      )}
                    </div>
                    {m.employeeIds.length > 0 ? (
                      <div className="flex flex-wrap gap-2 ml-6">
                        {m.employeeIds.map((id) => {
                          const emp = employees?.find((e) => e.id === id);
                          if (!emp) return null;
                          return (
                            <div
                              key={id}
                              className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5"
                            >
                              <div
                                className="w-6 h-6 rounded-full text-white text-[9.5px] font-semibold flex items-center justify-center"
                                style={{ backgroundColor: emp.color }}
                              >
                                {emp.firstName[0]}{emp.lastName[0]}
                              </div>
                              <span className="text-[12px] font-medium">{emp.firstName} {emp.lastName[0]}.</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-[11.5px] text-slate-500 italic ml-6">
                        Personne d'assigné — voir avec le titulaire
                      </div>
                    )}
                  </div>
                );
              })}
              {missions.length === 0 && !isLoading && (
                <div className="px-5 py-8 text-center text-[12px] text-slate-500">
                  Aucune mission configurée pour ce matin
                </div>
              )}
            </div>
          </div>

          {/* Présents */}
          <div className="space-y-3">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <div className="text-[13px] font-semibold">Équipe présente</div>
              </div>
              <div className="divide-y divide-slate-100">
                {presentIds.map((id) => {
                  const emp = employees?.find((e) => e.id === id);
                  if (!emp) return null;
                  return (
                    <div key={id} className="flex items-center gap-2.5 px-4 py-2.5">
                      <div
                        className="w-7 h-7 rounded-full text-white text-[10px] font-semibold flex items-center justify-center"
                        style={{ backgroundColor: emp.color }}
                      >
                        {emp.firstName[0]}{emp.lastName[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-medium truncate">
                          {emp.firstName} {emp.lastName}
                        </div>
                        <div className="text-[10px] text-slate-500">{emp.position}</div>
                      </div>
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200 bg-white/80 px-5 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <Lock size={11} className="text-slate-400" />
          <span>Mode équipe — aucune donnée personnelle accessible</span>
        </div>
      </div>
    </div>
  );
}
